import { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, If } from '@fashionnovateam/nova-ui-toolkit';
import {
  useGetMetaDatas,
  baseKeys,
} from '../../../../Lib/Hooks/MetadataService';
import { useSubmitItemDetails } from '../../../../Lib/Hooks/ItemService/useSubmitItemDetails';
import { useGetVendorItem } from '../../../../Lib/Hooks/ItemService/useGetVendorItem';
import { ShoeDetailsSection } from './ShoeDetailsSection';
import { ItemDetailsFormContext } from './useItemDetailForm';
import { FabricDetailsSection } from './FabricDetailsSection';
import { FitDetailsSection } from './FitDetailsSection';
import { StockDetailsSection } from './StockDetailsSection';
import { LogisticsSection } from './LogisticsSection';
import { CategoryDetailsSection } from './CategoryDetailsSection';
import { ItemDetailsFormData } from './types';
import { ItemFilters } from '../../../../Lib/Hooks/ItemService/enum';
import { mapTargetItemToFormValues } from './mapTargetItemToFormValues';
import { useSaveVendorCatalogItem } from '../../../../Lib/Hooks/ItemService/useSaveVendorCatalogItem';
import { UpdateItem } from '../../../../Lib/Api/item-service';
import { attributesToField } from './attributesToFieldMapping';

function ItemDetailsForm() {
  const { id: currentUserId, itemId: currentItemId } = useParams<{
    id: string;
    itemId: string;
  }>();
  const { data: targetItem } = useGetVendorItem(currentUserId, currentItemId);

  const methods = useForm<ItemDetailsFormData>({
    defaultValues: mapTargetItemToFormValues(targetItem),
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (targetItem) {
      methods.reset(mapTargetItemToFormValues(targetItem));
    }
  }, [targetItem, methods]);

  const { handleSubmit, watch, formState, setValue } = methods;

  function scrollToFirstError(errors: Record<string, unknown>) {
    if (!errors) return;
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`,
      );
      if (errorElement && typeof errorElement.scrollIntoView === 'function') {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof (errorElement as HTMLElement).focus === 'function') {
          (errorElement as HTMLElement).focus();
        }
      }
    }
  }

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      scrollToFirstError(formState.errors);
    }
  }, [formState.isSubmitting]);

  const { data: metaDatas } = useGetMetaDatas([
    'ns_master_details',
    'ns_master_neckline',
    'ns_master_sleeveLength',
    'ns_master_length',
    'ns_master_fits',
    'ns_master_fits2',
    'ns_master_length',
    'ns_master_patterns',
    'ns_master_wash',
    'ns_master_heelHeight',
    'ns_master_heelType',
    'ns_master_shoeStyle',
    'ns_master_merchHierarchy',
    'ns_master_fabrics',
    'ns_master_fieldListOptionsPerMerchCategory',
    'attributes_required',
  ]);

  const { isLoading, mutate: submitItemDetails } = useSubmitItemDetails();
  const { mutate: saveItem } = useSaveVendorCatalogItem();

  const currentDivision = watch('division');
  const currentCategory = watch('category');
  const currentClass = watch('class');
  const currentDepartment = watch('department');

  const fieldsListsPerCategory: { [key: string]: string[] } | null =
    useMemo(() => {
      if (!metaDatas?.ns_master_fieldListOptionsPerMerchCategory) return null;
      const divisions = metaDatas?.ns_master_merchHierarchy.divisions;
      if (!divisions) return null;

      const key = `${currentDivision} : ${currentDepartment} : ${currentCategory}`;
      return metaDatas.ns_master_fieldListOptionsPerMerchCategory[key];
    }, [
      currentCategory,
      currentDivision,
      currentDepartment,
      metaDatas?.ns_master_merchHierarchy,
      metaDatas?.ns_master_fieldListOptionsPerMerchCategory,
    ]);

  const makeOptionsFromMetaData = useCallback(
    (metaDataKey: (typeof baseKeys)[number], fieldsListKey?: string) => {
      if (fieldsListsPerCategory && fieldsListKey) {
        const fieldsList = fieldsListsPerCategory[fieldsListKey];
        if (!fieldsList) return [];
        return fieldsList.map((value: string) => ({
          label: value,
          value,
        }));
      }
      if (!metaDatas || !metaDataKey) return [];
      const castValue = metaDatas[metaDataKey] as string[];
      if (!castValue) return [];
      const uniqueValues = Array.from(new Set(castValue));

      return uniqueValues.map((value) => ({
        label: value,
        value,
      }));
    },
    [metaDatas, fieldsListsPerCategory],
  );

  const onSubmit = (data: ItemDetailsFormData) => {
    submitItemDetails({
      userId: currentUserId,
      itemId: currentItemId,
      itemDetails: {
        item_fields: {
          jr_vendor_ref: data.jrVendorReference,
          plus_vendor_ref: data.plusVendorReference,
          style_number: data.styleNumber,
          department: data.department,
          category: data.category,
          class: data.class,
          lead_time_in_weeks: data.leadTime,
          division: data.division,
          fashionnova_exclusive: data.fashionnovaExclusive,
          reorder_lead_time_in_weeks: data.reorderLeadTime,
          stock_quantity: data.stockQuantity,
          stock_ratio: data.stockRatio,
          stock_ihd: data.stockIhd ? new Date(data.stockIhd) : null,
          incoming_stock_quantity: data.incomingStockQuantity,
          incoming_stock_ratio: data.incomingStockRatio,
          incoming_stock_date: data.incomingStockDate
            ? new Date(data.incomingStockDate)
            : null,
          length: data.length,
          inseam: data.inseam,
          stretch: data.stretch,
          neckline: data.neckline,
          sleeve_length: data.sleeveLength,
          top_length: data.topLength,
          body_fit_top: data.bodyFitTop,
          body_fit_bottom: data.bodyFitBottom,
          bottom_length: data.bottomLength,
          fabric_weight: data.fabricWeight,
          fabric_code_and_name: data.fabricCode,
          fabric: data.fabric,
          pattern_print: data.patternPrint,
          wash: data.wash,
          heel_height: data.heelHeight,
          heel_type: data.heelType,
          shoe_style: data.shoeStyle,
        },
        draft_po_fields: {
          due_date: data.cancelDate ? new Date(data.cancelDate) : null,
          incoterm: data.incoterm,
          shipment_mode: data.shipmentMode,
          country_of_origin: data.countryOfOrigin,
        },
      },
    });
  };

  const attributesRules = useMemo(() => {
    const divisions = metaDatas?.ns_master_merchHierarchy?.divisions;
    if (!divisions) return null;

    const key = `${currentDivision} : ${currentDepartment} : ${currentCategory} : ${currentClass}`;
    const itemFormFields = metaDatas?.attributes_required;
    if (!itemFormFields) return null;
    return itemFormFields[key];
  }, [
    metaDatas,
    currentDivision,
    currentCategory,
    currentClass,
    currentDepartment,
  ]);

  const isFieldDisabled = useCallback(
    (field: string, defaultValue: boolean) => {
      if (!attributesRules) return defaultValue;
      return attributesRules[field] === 'disabled';
    },
    [attributesRules],
  );

  /**
   * Reset values of dynamically disabled fields whenever attribute rules change.
   * in `attributesToField`, this effect sets that field's value to `null` without
   * marking it as dirty. This keeps the form state consistent with the latest
   * attribute configuration and prevents disabled fields from retaining stale values.
   *
   * Dependencies:
   *  - Runs whenever `attributesRules` changes.
   */
  useEffect(() => {
    if (!attributesRules) return;

    for (const [attribute, rule] of Object.entries(attributesRules)) {
      if (rule === 'disabled' && attributesToField?.[attribute]) {
        setValue(attributesToField[attribute], null, {
          shouldDirty: false,
        });
      }
    }
  }, [attributesRules]);

  const isFieldRequired = useCallback(
    (field: string, defaultValue: boolean) => {
      if (!attributesRules) return defaultValue;
      return attributesRules[field] === 'required';
    },
    [attributesRules],
  );

  const contextValue = useMemo(
    () => ({ isFieldDisabled, isFieldRequired, makeOptionsFromMetaData }),
    [
      attributesRules,
      isFieldDisabled,
      isFieldRequired,
      makeOptionsFromMetaData,
    ],
  );

  const autoSave = useCallback(
    (data: UpdateItem) => {
      saveItem({
        userId: currentUserId,
        itemId: currentItemId,
        data,
      });
    },
    [saveItem, currentItemId, currentUserId],
  );

  const handleDebounceChange = useMemo(
    () =>
      debounce(() => {
        const formData = methods.getValues();
        autoSave({
          jr_vendor_ref: formData.jrVendorReference,
          plus_vendor_ref: formData.plusVendorReference,
          style_number: formData.styleNumber,
          category: formData.category,
          department: formData.department,
          class: formData.class,
          lead_time_in_weeks: formData.leadTime,
          division: formData.division,
          fashionnova_exclusive: formData.fashionnovaExclusive,
          reorder_lead_time_in_weeks: formData.reorderLeadTime,
          stock_quantity: formData.stockQuantity,
          stock_ratio: formData.stockRatio,
          stock_ihd: formData.stockIhd
            ? new Date(formData.stockIhd)
            : undefined,
          incoming_stock_quantity: formData.incomingStockQuantity,
          incoming_stock_ratio: formData.incomingStockRatio,
          incoming_stock_date: formData.incomingStockDate
            ? new Date(formData.incomingStockDate)
            : undefined,
          length: formData.length,
          inseam: formData.inseam,
          stretch: formData.stretch,
          neckline: formData.neckline,
          sleeve_length: formData.sleeveLength,
          top_length: formData.topLength,
          body_fit_top: formData.bodyFitTop,
          body_fit_bottom: formData.bodyFitBottom,
          bottom_length: formData.bottomLength,
          fabric_weight: formData.fabricWeight,
          fabric_code_and_name: formData.fabricCode,
          fabric: formData.fabric,
          pattern_print: formData.patternPrint,
          wash: formData.wash,
          heel_height: formData.heelHeight,
          heel_type: formData.heelType,
          shoe_style: formData.shoeStyle,
          draft_purchase_order: {
            incoterm: formData.incoterm,
            shipment_mode: formData.shipmentMode,
            country_of_origin: formData.countryOfOrigin,
            due_date: formData.cancelDate
              ? new Date(formData.cancelDate)
              : undefined,
          },
        });
      }, 500),
    [methods, autoSave],
  );

  const isShoeCategory = useMemo(
    () => currentCategory?.toLowerCase().includes('shoe'),
    [currentCategory],
  );

  return (
    <FormProvider {...methods}>
      <ItemDetailsFormContext.Provider value={contextValue}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          onBlur={handleDebounceChange}
          className="flex flex-col gap-8 bg-white rounded-lg p-6 mb-4"
          data-testid="item-details-form"
          noValidate
        >
          <span className="text-sm">Purchase Order</span>
          <CategoryDetailsSection />
          <LogisticsSection />
          <StockDetailsSection />
          <FitDetailsSection />
          <FabricDetailsSection />
          <If condition={isShoeCategory}>
            <ShoeDetailsSection />
          </If>
          {targetItem?.stage === ItemFilters.PENDING_VENDOR && (
            <Button loading={isLoading} type="submit">
              Submit
            </Button>
          )}
        </form>
      </ItemDetailsFormContext.Provider>
    </FormProvider>
  );
}

export default ItemDetailsForm;
